import ReactGA from 'react-ga';
import { useEffect, useRef } from 'react';
import { isWebComponentFlow } from './commonCode';
import { wcApiUrl } from '../WebComponents/commonComponents/customUtils';

const ANALYTICS_KEY = 'DS_APP_ANALYTICS';
const getAnalyticsState = () => {
  if (!window[ANALYTICS_KEY]) {
    window[ANALYTICS_KEY] = {
      gaInitialized: false,
      gaReady: false,
      gtmInitialized: false,
      reactGaInitialized: false,
    };
  }

  return window[ANALYTICS_KEY];
};

const Analytics = (props) => {
  const baseContext = isWebComponentFlow();
  const { initiateUiResponse, setGoogleCID, environment, wcFlow } = baseContext;
  const gaReadyRef = useRef(false);
  const isCancelledRef = useRef(false);
  const analyticsState = getAnalyticsState();

  const getGATrackingID = () =>
    wcFlow
      ? wcApiUrl(environment).REACT_APP_GA_TRACKING_ID[environment]
      : initiateUiResponse?.REACT_APP_GA_TRACKING_ID;

  const getGTMTrackingID = () =>
    wcFlow
      ? wcApiUrl(environment).REACT_APP_GTM_ID[environment]
      : initiateUiResponse?.REACT_APP_GTM_ID;

  const envCondition = wcFlow
    ? environment === 'dev' || environment === 'prod'
    : initiateUiResponse?.REACT_APP_ENVIRONMENT === 'production' ||
      initiateUiResponse?.REACT_APP_ENVIRONMENT === 'development';

  const initGA = (id) => {
    if (!id || analyticsState.gaInitialized) {
      return;
    }

    analyticsState.gaInitialized = true;
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    }
    if (typeof window.gtag !== 'function') {
      window.gtag = gtag;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    script.onload = () => {
      window.gtag('js', new Date());
      window.gtag('config', id);
      analyticsState.gaReady = true;
      gaReadyRef.current = true;
    };
    script.onerror = () => {
      analyticsState.gaInitialized = false;
      analyticsState.gaReady = false;
      gaReadyRef.current = false;
    };
    document.head.appendChild(script);
  };

  const initGTM = ({ gtmId, dataLayerName = 'dataLayer' }) => {
    if (!gtmId || analyticsState.gtmInitialized) {
      return;
    }

    analyticsState.gtmInitialized = true;
    window[dataLayerName] = window[dataLayerName] || [];
    window[dataLayerName].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}${
      dataLayerName !== 'dataLayer' ? `&l=${dataLayerName}` : ''
    }`;
    script.onerror = () => {
      analyticsState.gtmInitialized = false;
    };
    document.head.appendChild(script);
  };

  const captureCID = () => {
    let attempts = 0;
    const maxAttempts = 100;

    const getCIDFromCookie = () => {
      const gaCookie = document.cookie.split('; ').find((cookie) => cookie.startsWith('_ga='));

      if (!gaCookie) {
        return null;
      }

      const cookieValue = decodeURIComponent(gaCookie.substring('_ga='.length));
      const parts = cookieValue.split('.');

      if (parts.length < 4) {
        return null;
      }

      return `${parts[2]}.${parts[3]}`;
    };

    const checkCID = () => {
      if (isCancelledRef.current) return;

      attempts += 1;

      const clientId = getCIDFromCookie();

      if (clientId) {
        setGoogleCID(clientId);
        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(checkCID, 150);
      }
    };
    checkCID();
  };

  useEffect(() => {
    isCancelledRef.current = false;

    if (getGATrackingID()) {
      initGA(getGATrackingID());
    }
    if (envCondition) {
      // Replaced react-gtm-module to avoid its inline GTM bootstrap script, which violates CSP.
      initGTM({
        gtmId: getGTMTrackingID(),
      });

      const tryInitReactGA = () => {
        if (isCancelledRef.current) return;
        if (analyticsState.gaReady) {
          gaReadyRef.current = true;
        }

        if (!gaReadyRef.current) {
          setTimeout(tryInitReactGA, 50);
          return;
        }

        if (!analyticsState.reactGaInitialized) {
          ReactGA.initialize(getGATrackingID());
          analyticsState.reactGaInitialized = true;
        }
        ReactGA.pageview(window.location.pathname + window.location.search);
      };
      tryInitReactGA();
    }

    captureCID();

    return () => {
      isCancelledRef.current = true;
    };
  }, [initiateUiResponse]);

  return null;
};

export default Analytics;
